import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChallengeData } from '../services/challengeService';
import { Swords, Trophy, Target, Frown } from 'lucide-react';
import { getAvatarUrl } from '../utils/avatarUtils';
import { SafeImage } from './common/SafeImage';

interface ChallengeSummaryListProps {
  activeChallenges: ChallengeData[];
  completedChallenges: ChallengeData[];
  currentUser: any;
}

export default function ChallengeSummaryList({ activeChallenges, completedChallenges, currentUser }: ChallengeSummaryListProps) {
  const navigate = useNavigate();

  // Rule: Show max 2. Prioritize active. 
  // If 2 active -> show 2 active. 
  // If 1 active -> show 1 active, 1 finished. 
  // If 0 active -> show 2 finished.
  const displayChallenges = useMemo(() => {
    let toShow: { challenge: ChallengeData, isActive: boolean }[] = [];
    
    // Add active challenges (up to 2)
    for (let i = 0; i < Math.min(2, activeChallenges.length); i++) {
      toShow.push({ challenge: activeChallenges[i], isActive: true });
    }
    
    // Fill remaining spots (if any) with completed challenges
    let i = 0;
    while (toShow.length < 2 && i < completedChallenges.length) {
      toShow.push({ challenge: completedChallenges[i], isActive: false });
      i++;
    }
    
    return toShow;
  }, [activeChallenges, completedChallenges]);

  if (displayChallenges.length === 0) return null;

  return (
    <div className="w-full mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Target className="text-indigo-500 w-6 h-6" /> Recent Challenges
        </h2>
        <button 
          onClick={() => navigate('/challenges')}
          className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          View all
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayChallenges.map(({ challenge, isActive }) => {
          const isChallenger = currentUser.uid === challenge.challengerId;
          const myScore = Math.round(isChallenger ? challenge.challengerScore : challenge.challengedScore);
          const theirScore = Math.round(isChallenger ? challenge.challengedScore : challenge.challengerScore);
          const theirName = isChallenger ? challenge.challengedName : challenge.challengerName;
          const theirPhoto = isChallenger ? challenge.challengedPhoto : challenge.challengerPhoto;
          
          const typeLabel = challenge.challengeType === 'crime_game' ? 'Crime Game' :
                            challenge.challengeType === 'daily_training' ? 'Training' :
                            challenge.challengeType === 'word_of_day' ? 'Word of Day' : 'Point Race';
          
          const myAvatarUrl = currentUser.photoURL || getAvatarUrl(currentUser.uid);
          const theirAvatarUrl = theirPhoto || getAvatarUrl(theirName || 'opponent');

          if (isActive) {
            const timeLeftMs = challenge.endTime - Date.now();
            const hoursLeft = Math.floor(timeLeftMs / (1000 * 60 * 60));
            const minsLeft = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
            const isWinning = myScore > theirScore;
            const isLosing = myScore < theirScore;

            return (
              <div 
                key={challenge.id} 
                onClick={() => navigate(`/challenges/${challenge.id}`)}
                className="relative overflow-hidden rounded-2xl border-2 border-indigo-100 bg-white p-4 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="absolute bottom-0 left-0 h-1 bg-gray-100 w-full">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" 
                    style={{ width: `${Math.max(5, (timeLeftMs / (24 * 60 * 60 * 1000)) * 100)}%` }}
                  />
                </div>
                
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-1 rounded border border-indigo-100">
                    Active: {typeLabel} • {new Date(challenge.startTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                  <span className="text-xs font-semibold text-gray-500 flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                    ⏱ {hoursLeft}h {minsLeft}m
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-center flex-1">
                    <SafeImage src={myAvatarUrl} fallbackSeed={currentUser.uid} className={`w-12 h-12 rounded-full object-cover shadow-sm border-2 ${isWinning ? 'border-green-400' : 'border-gray-100'}`} alt="You" />
                    <span className="font-bold text-xl mt-1 text-gray-800">{myScore}</span>
                  </div>
                  
                  <div className="flex flex-col items-center px-2">
                    <Swords className="w-5 h-5 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />
                    {isWinning && <span className="text-[10px] font-bold text-green-500 uppercase">Winning</span>}
                    {isLosing && <span className="text-[10px] font-bold text-red-500 uppercase">Trailing</span>}
                  </div>
                  
                  <div className="flex flex-col items-center flex-1">
                    <SafeImage src={theirAvatarUrl} fallbackSeed={theirName || 'Opponent'} className={`w-12 h-12 rounded-full object-cover shadow-sm border-2 ${isLosing ? 'border-green-400' : 'border-gray-100'}`} alt={theirName || 'Opponent'} />
                    <span className="font-bold text-xl mt-1 text-gray-800">{theirScore}</span>
                  </div>
                </div>
                <button
                   onClick={(e) => {
                     e.stopPropagation();
                     navigate(`/challenges/${challenge.id}`);
                   }}
                   className="mt-3 w-full py-1.5 bg-indigo-50 text-indigo-600 font-semibold text-sm rounded-lg hover:bg-indigo-100 transition-colors"
                >
                   View Challenge
                </button>
              </div>
            );
          } else {
            const iWon = challenge.winnerId === currentUser.uid;
            const isDraw = !challenge.winnerId;

            return (
              <div 
                key={challenge.id} 
                onClick={() => navigate(`/challenges/${challenge.id}`)}
                className="bg-white rounded-2xl p-4 border-2 border-gray-100 shadow-sm hover:border-gray-300 transition-all cursor-pointer group"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Finished: {typeLabel} • {new Date(challenge.startTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${iWon ? 'text-green-600 bg-green-50' : isDraw ? 'text-gray-600 bg-gray-50' : 'text-red-600 bg-red-50'}`}>
                    {iWon ? 'Victory' : isDraw ? 'Draw' : 'Defeat'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex flex-col items-center flex-1">
                    <SafeImage src={myAvatarUrl} fallbackSeed={currentUser.uid} className={`w-12 h-12 rounded-full object-cover shadow-sm border-2 ${iWon ? 'border-green-400' : 'border-gray-100'}`} alt="You" />
                    <span className="font-bold text-xl mt-1 text-gray-800">{myScore}</span>
                  </div>
                  
                  <div className="flex flex-col items-center px-2">
                    {iWon ? <Trophy className="w-5 h-5 text-yellow-500 mb-1 group-hover:scale-110 transition-transform" /> : isDraw ? <Swords className="w-5 h-5 text-gray-400 mb-1 group-hover:scale-110 transition-transform" /> : <Frown className="w-5 h-5 text-gray-400 mb-1 group-hover:scale-110 transition-transform" />}
                  </div>
                  
                  <div className="flex flex-col items-center flex-1">
                    <SafeImage src={theirAvatarUrl} fallbackSeed={theirName || 'Opponent'} className={`w-12 h-12 rounded-full object-cover shadow-sm border-2 ${!iWon && !isDraw ? 'border-green-400' : 'border-gray-100'}`} alt={theirName || 'Opponent'} />
                    <span className="font-bold text-xl mt-1 text-gray-800">{theirScore}</span>
                  </div>
                </div>
                <button
                   onClick={(e) => {
                     e.stopPropagation();
                     navigate(`/challenges/${challenge.id}`);
                   }}
                   className="mt-3 w-full py-1.5 bg-gray-50 text-gray-600 font-semibold text-sm rounded-lg hover:bg-gray-100 transition-colors"
                >
                   View Result
                </button>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
}
